import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreateEmbedWorkflowNode,
  EmbedWorkflowConfiguration,
  RevisedWorkflow,
  Workflow,
  WorkflowEdge,
  WorkflowNode,
  WorkflowNodeType,
} from '../../../api/model.ts';
import { applyEdgeChanges, Connection, Controls, Edge, EdgeChange, Node, Panel, ReactFlow } from 'reactflow';
import { Tooltip } from '@mui/material';
import CustomEdge from '../canvas/edges/CustomEdge.tsx';
import { NodeTypes } from '../../../types.ts';
import FormNode from '../canvas/nodes/FormNode.tsx';
import FallbackNode from '../canvas/nodes/FallbackNode.tsx';
import { getAllNodesKeys, NodeFormProps } from '../../../api/workflows-steps/formSchema.tsx';
import { useStore } from '../../../store/store.ts';
import uuid from 'react-uuid';
import FormProxyNode from '../canvas/nodes/FormProxyNode.tsx';
import { Visibility } from '@mui/icons-material';
import { useNodeValidation } from 'hooks/useNodeValidation.tsx';
import useRevisedWorkflows from 'api/workflows/useRevisedWorkflows.ts';
import SubworkflowCommonForm from './SubworkflowCommonForm.tsx';
import { getRevisedNodesMapping } from 'api/workflows/requests.ts';
import { JumpToWorkflow } from 'components/button/JumpToWorkflow.tsx';
import { SectionTitle } from 'components/form/SectionTitle.tsx';
import { CanvasPreviewContainer, PreviewToggle } from './Form.styles.tsx';

type IoNodes = {
  inputs: WorkflowNode[];
  outputs: WorkflowNode[];
  providers: WorkflowNode[];
  consumers: WorkflowNode[];
};

const edgeTypes = {
  default: CustomEdge,
};

const providerNodesData = { source: true, target: false, highlightColor: '#9B6DE0' };
const inputNodesData = { target: true, source: false, requiredConnection: 'target', highlightColor: '#9B6DE0' };
const outputNodesData = { source: true, target: false, highlightColor: '#4C60C9' };
const consumerNodesData = { target: true, source: false, requiredConnection: 'target', highlightColor: '#4C60C9' };

const boxOffsetX = 250.0;
const startingX = 50.0;
const nodeWidth = 90.0;
const boxMargin = 50.0;

const EmbedWorkflowForm = (props: NodeFormProps) => {
  const { nodeId, initialValues = {} as CreateEmbedWorkflowNode } = props;
  const [formEdges, setFormEdges] = useState<Edge[]>([]);

  const [previewWorkflow, setPreviewWorkflow] = useState<boolean>(false);

  const initialConfiguration = initialValues?.configuration as EmbedWorkflowConfiguration;
  const { mainReactFlowInstance, setFormReactFlowInstance } = useStore();
  const [formValues, setFormValues] = useState<CreateEmbedWorkflowNode>(initialValues as CreateEmbedWorkflowNode);

  const { validate } = useNodeValidation(initialValues.type);

  const usedEmbedWorkflows: RevisedWorkflow[] = useMemo(() => {
    if (!formValues?.configuration?.workflow_id || !formValues?.configuration?.revision) {
      return [];
    }
    const selectedWorkflows =
      mainReactFlowInstance
        ?.getNodes()
        .filter((n) => n.type === 'embed_workflow' && n.id !== nodeId)
        .map((w) => ({
          workflow_id: w.data.configuration.workflow_id,
          revision: w.data.configuration.revision,
        })) ?? [];
    selectedWorkflows.push({
      workflow_id: formValues?.configuration?.workflow_id,
      revision: formValues?.configuration?.revision,
    });
    return selectedWorkflows;
  }, [mainReactFlowInstance, formValues?.configuration?.workflow_id, formValues?.configuration?.revision]);

  const { revisedWorkflowsData: workflows } = useRevisedWorkflows({
    revisedWorkflows: usedEmbedWorkflows,
  });

  const getWorkflowById = (selectedWorkflowId: string) => workflows?.find((wf) => wf.id === selectedWorkflowId) ?? null;

  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    getWorkflowById(initialConfiguration?.workflow_id ?? ''),
  );
  useEffect(() => {
    if (isCreate || !initialValues?.configuration) {
      return;
    }
    const embedWorkflowConfiguration = initialValues.configuration as EmbedWorkflowConfiguration;
    const initialEdges: Edge[] = [
      ...embedWorkflowConfiguration.input_node_mappings.map(
        (input) =>
          ({
            source: input.parent_node_id,
            target: input.child_node_id,
            id: uuid(),
            deletable: !props.disabled,
          }) as Edge,
      ),
      ...embedWorkflowConfiguration.output_node_mappings.map(
        (output) =>
          ({
            source: output.child_node_id,
            target: output.parent_node_id,
            id: uuid(),
            deletable: !props.disabled,
          }) as Edge,
      ),
    ];
    setFormEdges(initialEdges);
  }, [initialValues?.configuration]);

  useEffect(() => {
    if (workflows && formValues?.configuration?.workflow_id) {
      setSelectedWorkflow(getWorkflowById(formValues?.configuration.workflow_id));
    }
  }, [formValues?.configuration?.workflow_id, formValues?.configuration?.revision, workflows]);

  const ioNodes = useMemo(() => {
    const io: IoNodes = {
      inputs: [],
      outputs: [],
      providers: [],
      consumers: [],
    };
    if (!selectedWorkflow || !mainReactFlowInstance || !nodeId) {
      return io;
    }
    const sourceIds = new Set(selectedWorkflow.edges.map((edge) => edge.source));
    const targetIds = new Set(selectedWorkflow.edges.map((edge) => edge.target));
    selectedWorkflow.nodes.forEach((node) => {
      if (!sourceIds.has(node.id)) {
        io.outputs.push(node);
      }
      if (!targetIds.has(node.id)) {
        io.inputs.push(node);
      }
    });

    const edges = mainReactFlowInstance.getEdges();
    edges.forEach((edge) => {
      if (edge.target === nodeId) {
        const node = mainReactFlowInstance.getNode(edge.source);
        node && io.providers.push({ ...node, ...node.data });
      } else if (edge.source === nodeId) {
        const node = mainReactFlowInstance.getNode(edge.target);
        node && io.consumers.push({ ...node, ...node.data });
      }
    });
    return io;
  }, [selectedWorkflow, nodeId, mainReactFlowInstance, JSON.stringify(mainReactFlowInstance?.getEdges())]);

  const validFormEdges = useMemo(
    () =>
      formEdges.filter(
        (edge) => ioNodes.inputs.find((n) => n.id === edge.target) || ioNodes.outputs.find((n) => n.id === edge.source),
      ),
    [formEdges, ioNodes],
  );

  useEffect(() => {
    const consumersNodeIds = ioNodes.consumers.map((n) => n.id);
    const providersNodeIds = ioNodes.providers.map((n) => n.id);
    if (formEdges.length > 0 && formEdges.length !== consumersNodeIds.length + providersNodeIds.length) {
      let edges = formEdges.filter((e) => consumersNodeIds.includes(e.target) || providersNodeIds.includes(e.source));
      setFormEdges(edges);
    }
  }, [ioNodes]);

  const updateFormValues = (newValues: CreateEmbedWorkflowNode) => {
    setFormValues(newValues);
    props.onChange?.(newValues);
  };

  const handleFormChange = async (newValues: CreateEmbedWorkflowNode) => {
    const configuration = newValues?.configuration as EmbedWorkflowConfiguration;
    const selectedWorkflowId = configuration?.workflow_id;
    const revision = configuration?.revision;
    if (isCreate || !selectedWorkflowId) {
      updateFormValues(newValues);
      return;
    }

    if (selectedWorkflowId !== selectedWorkflow?.id) {
      setFormEdges([]);
      updateFormValues(newValues);
      return;
    }

    if (!revision || revision === selectedWorkflow?.revision) {
      updateFormValues(newValues);
      return;
    }

    const oldConfiguration = formValues?.configuration;
    const sourceRevision = oldConfiguration?.revision;
    if (!sourceRevision) {
      setFormEdges([]);
      updateFormValues(newValues);
      return;
    }

    const targetRevision = revision;
    const nodeIds =
      oldConfiguration?.input_node_mappings
        .map((mapping) => mapping.child_node_id)
        .concat(oldConfiguration?.output_node_mappings.map((mapping) => mapping.child_node_id)) ?? [];

    try {
      const mappingResponse = await getRevisedNodesMapping(selectedWorkflowId, sourceRevision, targetRevision, nodeIds);
      const revisedMapping = mappingResponse.mapping;

      configuration.input_node_mappings = configuration.input_node_mappings
        .map((mapping) => {
          const revisedNode = revisedMapping[mapping.child_node_id];
          return revisedNode ? { ...mapping, child_node_id: revisedNode.id } : null;
        })
        .filter((mapping) => mapping !== null);
      configuration.output_node_mappings = configuration.output_node_mappings
        .map((mapping) => {
          const revisedNode = revisedMapping[mapping.child_node_id];
          return revisedNode ? { ...mapping, child_node_id: revisedNode.id } : null;
        })
        .filter((mapping) => mapping !== null);

      setFormEdges((prevEdges) =>
        prevEdges
          .filter((edge) => {
            const isInputEdge = nodeIds.includes(edge.target) && revisedMapping[edge.target];
            const isOutputEdge = nodeIds.includes(edge.source) && revisedMapping[edge.source];
            return isInputEdge || isOutputEdge;
          })
          .map((edge) => {
            const revisedTarget = revisedMapping[edge.target];
            const revisedSource = revisedMapping[edge.source];
            return {
              ...edge,
              target: revisedTarget ? revisedTarget.id : edge.target,
              source: revisedSource ? revisedSource.id : edge.source,
            };
          }),
      );
    } catch (error) {
      console.error('Error fetching node revision mapping:', error);
      setFormEdges([]);
    }

    updateFormValues(newValues);
  };

  const isCreate = !initialValues?.name;

  const stepsConfig = getAllNodesKeys();

  const nodeTypes: NodeTypes = useMemo((): NodeTypes => {
    const types = stepsConfig.reduce(
      (obj, key: string) => ({
        ...obj,
        [key]: FormNode,
        proxy: FormProxyNode,
      }),
      {},
    ) as NodeTypes;
    return { ...types, ...{ default: FallbackNode } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderedNodes = useMemo(() => {
    const tempRenderedNodes = [];

    const nodeGroups = [
      { nodes: ioNodes.providers, data: providerNodesData },
      { nodes: ioNodes.inputs, data: inputNodesData },
      { nodes: ioNodes.outputs, data: outputNodesData },
      { nodes: ioNodes.consumers, data: consumerNodesData },
    ];

    tempRenderedNodes.push({
      id: 'background-node',
      type: 'group',
      position: {
        x: boxOffsetX,
        y: 0,
      },
      style: {
        width: 430,
        height: Math.max(ioNodes.inputs.length, ioNodes.outputs.length) * 130,
        backgroundColor: 'rgba(76, 96, 201, 0.1)',
        border: '1px solid rgba(76, 96, 201, 0.25)',
        borderRadius: 16,
        pointerEvents: 'none',
      },
    } as Node);

    nodeGroups.forEach((group, groupIndex) => {
      tempRenderedNodes.push(
        ...group.nodes.map((node, index) => {
          return {
            id: node.id,
            type: node.type,
            data: { ...node, ...group.data, edges: validFormEdges },
            position: { x: startingX + boxOffsetX * groupIndex, y: 130 * index + 20 },
          } as Node<WorkflowNode, WorkflowNodeType>;
        }),
      );
    });

    return tempRenderedNodes;
  }, [ioNodes, previewWorkflow, validFormEdges]);

  const calculateBoundingBox = useCallback((nodes: Node[]) => {
    if (nodes.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of nodes) {
      if (node.position.x < minX) minX = node.position.x;
      if (node.position.x > maxX) maxX = node.position.x;
      if (node.position.y < minY) minY = node.position.y;
      if (node.position.y > maxY) maxY = node.position.y;
    }

    return {
      x: minX - boxMargin,
      y: minY - boxMargin,
      width: maxX - minX + nodeWidth + boxMargin * 2,
      height: maxY - minY + nodeWidth + boxMargin * 2,
    };
  }, []);

  const workflowNodes = useMemo(() => {
    if (!selectedWorkflow) return [];

    const nodes =
      selectedWorkflow.nodes.map((node) => {
        const isInput = !!ioNodes.inputs.find((n) => n.id === node.id);
        const isOutput = !!ioNodes.outputs.find((n) => n.id === node.id);
        const extraData = isInput ? inputNodesData : isOutput ? outputNodesData : {};
        return {
          id: node.id,
          type: node.type,
          connectable: isInput || isOutput,
          data: { ...node, ...extraData, target: true, source: true, edges: formEdges },
          position: node.viewport_metadata?.position ?? { x: 0.0, y: 0.0 },
        } as Node<WorkflowNode, WorkflowNodeType>;
      }) ?? [];

    const bbox = calculateBoundingBox(nodes);
    const xOffset = bbox.x - boxOffsetX;
    const yOffset = bbox.y;
    return nodes.map((node) => {
      return {
        ...node,
        position: { x: node.position.x - xOffset, y: node.position.y - yOffset },
      };
    });
  }, [selectedWorkflow, formEdges]);

  const workflowEdges = useMemo(() => {
    if (!selectedWorkflow) return [];

    return (
      selectedWorkflow.edges.map((edge) => {
        return { ...edge, deletable: false } as Edge;
      }) ?? []
    );
  }, [selectedWorkflow]);

  const nodes = useMemo(() => {
    if (!previewWorkflow) {
      return renderedNodes;
    } else {
      const backgroundNode = renderedNodes.find((node) => node.id === 'background-node');
      if (!backgroundNode) return [];

      const bbox = calculateBoundingBox(workflowNodes);

      backgroundNode.position = { x: bbox.x, y: bbox.y };
      backgroundNode.style = { ...backgroundNode.style, width: bbox.width, height: bbox.height };

      const consumerNodes = renderedNodes.filter((node) => ioNodes.consumers.find((n) => n.id === node.id));
      for (const node of consumerNodes) {
        node.position.x = backgroundNode.position.x + (backgroundNode.style.width as number) + 120;
      }

      return [...renderedNodes, ...workflowNodes];
    }
  }, [previewWorkflow, renderedNodes, workflowNodes]);

  const edges = useMemo(() => {
    if (!previewWorkflow) {
      return validFormEdges;
    } else {
      return [...validFormEdges, ...workflowEdges];
    }
  }, [previewWorkflow, validFormEdges, workflowEdges]);

  const handleConnectNodes = ({ source, target }: Connection) => {
    const isSourceOutput = ioNodes.outputs.find((node) => node.id === source);
    const isTargetConsumer = ioNodes.consumers.find((node) => node.id === target);
    const isSourceProvider = ioNodes.providers.find((node) => node.id === source);
    const isTargetInput = ioNodes.inputs.find((node) => node.id === target);
    if ((isSourceOutput && isTargetConsumer) || (isSourceProvider && isTargetInput)) {
      const updatedEdges = formEdges.filter((edge) => edge.target !== target);

      const newEdge = {
        source,
        target,
        id: uuid(),
      } as Edge;

      setFormEdges([...updatedEdges, newEdge]);
    }
  };

  useEffect(() => {
    const configuration = { ...formValues?.configuration } as EmbedWorkflowConfiguration | undefined;
    if (!configuration) {
      return;
    }

    configuration.input_node_mappings = [];
    configuration.output_node_mappings = [];
    formEdges.forEach((edge) => {
      if (edge.deletable === false) {
        return;
      }
      const isInputEdge = ioNodes.inputs.find((node) => node.id === edge.target);
      if (isInputEdge) {
        configuration.input_node_mappings.push({
          parent_node_id: edge.source,
          child_node_id: edge.target,
        });
        return;
      }
      const isOutputEdge = ioNodes.outputs.find((node) => node.id === edge.source);
      if (isOutputEdge) {
        configuration.output_node_mappings.push({
          parent_node_id: edge.target,
          child_node_id: edge.source,
        });
      }
    });

    updateFormValues({ ...formValues, configuration });
  }, [formEdges, ioNodes]);

  const handleSubmit = useCallback(async () => {
    if (!isCreate) {
      const validationExtraData = {
        edges: mainReactFlowInstance
          ?.getEdges()
          .filter((e) => e.source === formValues.id || e.target === formValues.id) as WorkflowEdge[],
      };

      const isValid = await validate(formValues, validationExtraData);
      if (!isValid) {
        return;
      }
    }

    props.onSubmit?.(formValues);
  }, [formValues, mainReactFlowInstance, isCreate]);

  const handleOnEdgesChange = (changes: EdgeChange[]) => {
    const shouldUpdate = !!changes.filter((c) => c.type != 'select').length;

    if (shouldUpdate) {
      setFormEdges((prevEdges) => applyEdgeChanges(changes, prevEdges));
    }
  };

  return (
    <div className="flex flex-col">
      <SubworkflowCommonForm
        {...props}
        onSubmit={handleSubmit}
        onChange={(newFormData) => handleFormChange(newFormData as CreateEmbedWorkflowNode)}
        validate={false}
      />

      {!isCreate && !props.isTooltipView && (
        <>
          <div className="mb-5">
            <SectionTitle title="Embed workflow inputs & outputs" />
          </div>
          <CanvasPreviewContainer>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={() => {}}
              onEdgesChange={handleOnEdgesChange}
              onConnect={handleConnectNodes}
              onInit={(instance) => setFormReactFlowInstance(instance)}
              onDrop={() => {}}
              onDragOver={() => {}}
              proOptions={{ hideAttribution: true }}
              nodesConnectable={!props.disabled}
              nodesDraggable={false}
              edgesFocusable={!props.disabled}
              edgesUpdatable={!props.disabled}
              className="h-full"
            >
              <Panel position="top-right">
                <Tooltip title="Toggle workflow preview">
                  <PreviewToggle
                    value="check"
                    size="small"
                    selected={previewWorkflow}
                    onChange={() => setPreviewWorkflow((prev) => !prev)}
                  >
                    <Visibility />
                  </PreviewToggle>
                </Tooltip>
              </Panel>
              <Controls className="preview-flow-controls" position="bottom-right" showInteractive={false} />
            </ReactFlow>
          </CanvasPreviewContainer>
        </>
      )}

      {selectedWorkflow && (
        <div className={(isCreate ? 'mt-1' : 'mt-4') + ' flex justify-between'}>
          <JumpToWorkflow
            projectId={selectedWorkflow.project_id}
            workflowId={selectedWorkflow.id}
            revision={selectedWorkflow.revision}
          />
        </div>
      )}
    </div>
  );
};

export default EmbedWorkflowForm;
